// =============================================================
// Competitor Analysis — Phase 1 owner-only workspace.
//
// This page has TWO faces:
//   1. LockedComingSoon — shown to all non-owners while the global
//      feature-live flag is False. Mirrors the Email Marketing lock
//      visual language exactly (GlassCard, badge, feature bullets,
//      plan-aware hint).
//   2. CompetitorWorkspace — real 6-tab workspace for the owner
//      (justin.jg101@gmail.com). Every list is loaded on demand via
//      the /api/competitors[-*] endpoints and every write is
//      persisted server-side. No fake data, no fabricated metrics.
//
// Cross-feature actions (Generate Post From Insight, Send To
// Marketing Ideas) are rendered as *polished disabled future
// actions* per Phase 1 spec — they never fire and never mutate
// other modules.
// =============================================================
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  Lock,
  Plus,
  RefreshCw,
  Sparkles,
  Layers,
  Compass,
  Target,
  FileText,
  Trash2,
  Edit3,
  Save,
  X,
  ArrowRight,
  Info,
  Building2,
  Instagram,
  Facebook,
  Linkedin,
  MessageCircle,
  Twitter,
  ExternalLink,
  ShieldCheck,
  Loader2,
  ChevronRight,
  BarChart3,
  Lightbulb,
  Ban,
  CheckCircle2,
  Circle,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };
const CATEGORY_LABELS = {
  content_gap: "Content Gap",
  messaging: "Messaging Opportunity",
  differentiation: "Differentiation",
  campaign_idea: "Campaign Idea",
  audience: "Audience Opportunity",
  offer_cta: "Offer / CTA",
};
const SOCIAL_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  x: Twitter,
  tiktok: MessageCircle,
};

// =============================================================
// LOCKED (non-owner) view — mirrors Email Marketing lock exactly.
// =============================================================
function LockedComingSoon({ access }) {
  const plan = access?.plan || "free";
  const isBusinessTier = plan === "business";
  return (
    <div className="max-w-3xl mx-auto" data-testid="competitor-analysis-locked">
      <GlassCard className="p-8 text-center">
        <div className="mx-auto h-14 w-14 rounded-xl bg-[color:rgba(27,168,159,0.10)] flex items-center justify-center text-[color:var(--brand-primary)] mb-4">
          <Search className="h-7 w-7" />
        </div>
        <Badge variant="outline" className="mb-3 border-[color:var(--border-subtle)] text-[color:var(--text-2)]">
          <Lock className="h-3 w-3 mr-1" /> Coming Soon
        </Badge>
        <h2 className="font-display text-2xl font-semibold mb-2">Competitor Analysis</h2>
        <p className="text-sm text-[color:var(--text-2)] max-w-lg mx-auto">
          Get detailed AI-powered breakdowns of competitors — their strengths, weaknesses,
          and where your business can win.
        </p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
          {[
            { icon: Target, label: "Track competitor positioning" },
            { icon: Layers, label: "Compare content strategy" },
            { icon: Compass, label: "Spot market opportunities" },
            { icon: FileText, label: "Save analysis reports" },
          ].map((f) => (
            <div key={f.label} className="flex items-center gap-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2">
              <f.icon className="h-4 w-4 text-[color:var(--brand-primary)]" />
              <span className="text-sm text-[color:var(--text-2)]">{f.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 text-xs text-[color:var(--text-3)]" data-testid="competitor-analysis-locked-plan-hint">
          {isBusinessTier
            ? "Included with your plan when available."
            : "Coming Soon on Business."}
        </div>
      </GlassCard>
    </div>
  );
}

// =============================================================
// Small primitives
// =============================================================
function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <GlassCard className="p-10 text-center" data-testid="ca-empty-state">
      <div className="mx-auto h-14 w-14 rounded-xl bg-[color:rgba(27,168,159,0.10)] flex items-center justify-center text-[color:var(--brand-primary)] mb-4">
        <Icon className="h-7 w-7" />
      </div>
      <h3 className="font-display text-lg font-semibold mb-1">{title}</h3>
      {description ? <p className="text-sm text-[color:var(--text-2)] max-w-md mx-auto">{description}</p> : null}
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </GlassCard>
  );
}

function KpiCard({ label, value, hint, icon: Icon, testid }) {
  return (
    <div className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)]">
        {Icon ? <Icon className="h-3 w-3" /> : null}
        <span>{label}</span>
      </div>
      <div className="mt-1 font-display text-2xl font-semibold text-[color:var(--text-1)]">{value ?? "—"}</div>
      {hint ? <div className="text-xs text-[color:var(--text-3)] mt-1">{hint}</div> : null}
    </div>
  );
}

function PriorityBadge({ priority }) {
  const styles = {
    high: "bg-[color:rgba(255,138,61,0.10)] text-[color:var(--accent-warm)] border-[color:rgba(255,138,61,0.30)]",
    medium: "bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)] border-[color:rgba(27,168,159,0.30)]",
    low: "bg-[color:var(--surface-2)] text-[color:var(--text-3)] border-[color:var(--border-subtle)]",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${styles[priority] || styles.medium}`}>
      {priority || "medium"}
    </span>
  );
}

function AiSourcedNote() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-dashed border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]/40 px-3 py-2 text-xs text-[color:var(--text-3)]" data-testid="ca-ai-note">
      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <span>AI analysis based on available information. Postmoro has not accessed competitor websites or social profiles in Phase 1.</span>
    </div>
  );
}

// =============================================================
// Overview tab
// =============================================================
function OverviewTab({ setTab }) {
  const [stats, setStats] = useState(null);
  const [latest, setLatest] = useState(null);
  const [topOpps, setTopOpps] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c, opps] = await Promise.all([
        api.get("/competitors/overview"),
        api.get("/competitors"),
        api.get("/competitor-opportunities", { params: { state: "saved" } }),
      ]);
      setStats(o.data);
      setCompetitors(c.data?.competitors || []);
      const sortedOpps = [...(opps.data?.opportunities || [])].sort(
        (a, b) => (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3),
      );
      setTopOpps(sortedOpps.slice(0, 5));
      const cs = c.data?.competitors || [];
      const first = cs.find((x) => x.last_analyzed_at);
      if (first) {
        try {
          const a = await api.get(`/competitors/${first.id}/analysis`);
          if (a.data?.analysis) setLatest({ competitor: first, analysis: a.data.analysis });
        } catch (_e) { /* ignore */ }
      } else {
        setLatest(null);
      }
    } catch (_e) {
      /* handled by parent access gate */
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const hasCompetitors = competitors.length > 0;

  return (
    <div className="space-y-6" data-testid="ca-tab-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Competitors tracked" value={stats?.competitors_total ?? 0} icon={Target} testid="ca-kpi-competitors" />
        <KpiCard label="Analyses run" value={stats?.analyses_run ?? 0} icon={Sparkles} testid="ca-kpi-analyses" />
        <KpiCard label="Open opportunities" value={stats?.open_opportunities ?? 0} icon={Lightbulb} testid="ca-kpi-opportunities" />
        <KpiCard label="High-priority" value={stats?.high_priority_opportunities ?? 0} hint="of open opportunities" icon={Star} testid="ca-kpi-high" />
      </div>

      {!loading && !hasCompetitors ? (
        <EmptyState
          icon={Search}
          title="See how your business stacks up."
          description="Track competitors, compare positioning and content strategy, and turn competitive gaps into marketing opportunities."
          action={
            <Button data-testid="ca-overview-add-first" onClick={() => setTab("competitors")}>
              <Plus className="h-4 w-4 mr-1" /> Add your first competitor
            </Button>
          }
        />
      ) : null}

      {hasCompetitors ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <GlassCard className="p-5 lg:col-span-2" data-testid="ca-overview-snapshot">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-base font-semibold">Competitive snapshot</div>
              <Button variant="ghost" size="sm" onClick={() => setTab("competitors")}>
                Manage <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            <div className="space-y-2">
              {competitors.slice(0, 5).map((c) => (
                <div key={c.id} className="flex items-center gap-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2" data-testid={`ca-overview-competitor-${c.id}`}>
                  <div className="h-8 w-8 rounded-md bg-[color:var(--surface-1)] flex items-center justify-center text-[color:var(--text-2)]">
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-xs text-[color:var(--text-3)] truncate">
                      {c.industry || "—"}{c.location ? ` · ${c.location}` : ""}
                    </div>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">
                    {c.last_analyzed_at ? "Analyzed" : "Not analyzed"}
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard className="p-5" data-testid="ca-overview-insights">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-base font-semibold">Top opportunities</div>
              <Button variant="ghost" size="sm" onClick={() => setTab("opportunities")}>
                View all <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            {topOpps.length === 0 ? (
              <div className="text-sm text-[color:var(--text-3)]">Run an analysis to surface opportunities.</div>
            ) : (
              <ul className="space-y-2">
                {topOpps.map((o) => (
                  <li key={o.id} className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2" data-testid={`ca-overview-opp-${o.id}`}>
                    <div className="flex items-center gap-2">
                      <PriorityBadge priority={o.priority} />
                      <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">
                        {CATEGORY_LABELS[o.category] || o.category}
                      </span>
                    </div>
                    <div className="mt-1 text-sm font-medium">{o.title}</div>
                  </li>
                ))}
              </ul>
            )}
          </GlassCard>
        </div>
      ) : null}

      {latest ? (
        <GlassCard className="p-5" data-testid="ca-overview-latest">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)]">Latest analysis</div>
              <div className="font-display text-lg font-semibold">{latest.competitor.name}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setTab("competitors")}>
              Open profile <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <p className="text-sm text-[color:var(--text-2)]">{latest.analysis?.structured?.summary || "—"}</p>
          <div className="mt-3"><AiSourcedNote /></div>
        </GlassCard>
      ) : null}
    </div>
  );
}

// =============================================================
// Competitor editor dialog (create or edit)
// =============================================================
function CompetitorEditor({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: "", website_url: "", industry: "", location: "", notes: "",
    facebook: "", instagram: "", linkedin: "", x: "", tiktok: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: initial?.name || "",
      website_url: initial?.website_url || "",
      industry: initial?.industry || "",
      location: initial?.location || "",
      notes: initial?.notes || "",
      facebook: initial?.social_urls?.facebook || "",
      instagram: initial?.social_urls?.instagram || "",
      linkedin: initial?.social_urls?.linkedin || "",
      x: initial?.social_urls?.x || "",
      tiktok: initial?.social_urls?.tiktok || "",
    });
  }, [open, initial]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    const name = (form.name || "").trim();
    if (!name) { toast.error("Competitor name is required."); return; }
    setSaving(true);
    try {
      const body = {
        name,
        website_url: form.website_url.trim(),
        industry: form.industry.trim(),
        location: form.location.trim(),
        notes: form.notes.trim(),
        social_urls: {
          facebook: form.facebook.trim(),
          instagram: form.instagram.trim(),
          linkedin: form.linkedin.trim(),
          x: form.x.trim(),
          tiktok: form.tiktok.trim(),
        },
      };
      const r = isEdit
        ? await api.patch(`/competitors/${initial.id}`, body)
        : await api.post("/competitors", body);
      toast.success(isEdit ? "Competitor updated." : "Competitor added.");
      onSaved?.(r.data?.competitor);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save competitor.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[color:var(--bg-1)] border-[color:var(--border-subtle)]" data-testid="ca-competitor-editor">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit competitor" : "Add competitor"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label htmlFor="ca-name">Name <span className="text-[color:var(--accent-warm)]">*</span></Label>
            <Input id="ca-name" data-testid="ca-editor-name" value={form.name} onChange={set("name")} placeholder="e.g. Rival Roast Cafe" />
          </div>
          <div>
            <Label htmlFor="ca-website">Website URL</Label>
            <Input id="ca-website" data-testid="ca-editor-website" value={form.website_url} onChange={set("website_url")} placeholder="https://…" />
          </div>
          <div>
            <Label htmlFor="ca-industry">Industry / category</Label>
            <Input id="ca-industry" data-testid="ca-editor-industry" value={form.industry} onChange={set("industry")} placeholder="e.g. Coffee shop" />
          </div>
          <div>
            <Label htmlFor="ca-location">Location / market</Label>
            <Input id="ca-location" data-testid="ca-editor-location" value={form.location} onChange={set("location")} placeholder="e.g. Melbourne" />
          </div>
          <div>
            <Label htmlFor="ca-fb">Facebook URL</Label>
            <Input id="ca-fb" data-testid="ca-editor-facebook" value={form.facebook} onChange={set("facebook")} placeholder="https://facebook.com/…" />
          </div>
          <div>
            <Label htmlFor="ca-ig">Instagram URL</Label>
            <Input id="ca-ig" data-testid="ca-editor-instagram" value={form.instagram} onChange={set("instagram")} placeholder="https://instagram.com/…" />
          </div>
          <div>
            <Label htmlFor="ca-li">LinkedIn URL</Label>
            <Input id="ca-li" data-testid="ca-editor-linkedin" value={form.linkedin} onChange={set("linkedin")} placeholder="https://linkedin.com/company/…" />
          </div>
          <div>
            <Label htmlFor="ca-x">X URL</Label>
            <Input id="ca-x" data-testid="ca-editor-x" value={form.x} onChange={set("x")} placeholder="https://x.com/…" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="ca-tt">TikTok URL</Label>
            <Input id="ca-tt" data-testid="ca-editor-tiktok" value={form.tiktok} onChange={set("tiktok")} placeholder="https://tiktok.com/@…" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="ca-notes">Notes</Label>
            <Textarea id="ca-notes" data-testid="ca-editor-notes" value={form.notes} onChange={set("notes")} rows={4}
              placeholder="What do you know about them? Positioning, offers, tone, common formats, target audience…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="ca-editor-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ca-editor-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            {isEdit ? "Save changes" : "Add competitor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================
// Competitor profile drawer (opens inside Competitors tab)
// =============================================================
function CompetitorProfile({ competitor, onClose, onChanged }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!competitor?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/competitors/${competitor.id}/analysis`);
      setAnalysis(r.data?.analysis || null);
    } catch (_e) { setAnalysis(null); }
    finally { setLoading(false); }
  }, [competitor?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post(`/competitors/${competitor.id}/analyze`);
      setAnalysis(r.data?.analysis || null);
      toast.success("Analysis complete.");
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.error || "Analysis failed.");
      toast.error(msg);
    } finally { setAnalyzing(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/competitors/${competitor.id}`);
      toast.success("Competitor deleted.");
      onChanged?.();
      onClose?.();
    } catch (_e) { toast.error("Could not delete competitor."); }
  };

  const s = analysis?.structured || {};

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 w-full sm:max-w-2xl bg-[color:var(--bg-1)] border-l border-[color:var(--border-subtle)] shadow-xl overflow-y-auto" data-testid="ca-competitor-profile">
        <div className="sticky top-0 z-10 border-b border-[color:var(--border-subtle)] bg-[color:var(--bg-1)]/95 backdrop-blur px-5 py-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-[color:var(--surface-2)] flex items-center justify-center text-[color:var(--text-2)]">
            <Building2 className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-base font-semibold truncate">{competitor.name}</div>
            <div className="text-xs text-[color:var(--text-3)] truncate">
              {competitor.industry || "—"}{competitor.location ? ` · ${competitor.location}` : ""}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setEditorOpen(true)} data-testid="ca-profile-edit" aria-label="Edit">
            <Edit3 className="h-4 w-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="ca-profile-delete" aria-label="Delete">
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[color:var(--bg-1)] border-[color:var(--border-subtle)]">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete competitor?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes “{competitor.name}” and its saved analyses and opportunities. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="ca-profile-delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={remove} data-testid="ca-profile-delete-confirm">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close" data-testid="ca-profile-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="px-5 py-5 space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            {competitor.website_url ? (
              <a href={competitor.website_url} target="_blank" rel="noopener noreferrer" className="text-xs inline-flex items-center gap-1 rounded-md border border-[color:var(--border-subtle)] px-2 py-1 hover:bg-[color:var(--surface-2)]" data-testid="ca-profile-website">
                <ExternalLink className="h-3 w-3" /> Website
              </a>
            ) : null}
            {Object.entries(competitor.social_urls || {}).filter(([, v]) => v).map(([k, v]) => {
              const Icon = SOCIAL_ICONS[k] || ExternalLink;
              return (
                <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="text-xs inline-flex items-center gap-1 rounded-md border border-[color:var(--border-subtle)] px-2 py-1 hover:bg-[color:var(--surface-2)] capitalize" data-testid={`ca-profile-social-${k}`}>
                  <Icon className="h-3 w-3" /> {k}
                </a>
              );
            })}
          </div>

          {competitor.notes ? (
            <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-3 text-sm text-[color:var(--text-2)] whitespace-pre-wrap" data-testid="ca-profile-notes">
              {competitor.notes}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={analyze} disabled={analyzing} data-testid="ca-profile-analyze">
              {analyzing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
              {analysis ? "Refresh analysis" : "Run AI analysis"}
            </Button>
            {competitor.last_analyzed_at ? (
              <span className="text-xs text-[color:var(--text-3)]">
                Last analyzed {new Date(competitor.last_analyzed_at).toLocaleString()}
              </span>
            ) : null}
          </div>

          {loading ? (
            <div className="text-sm text-[color:var(--text-3)] flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading analysis…</div>
          ) : !analysis ? (
            <EmptyState
              icon={Sparkles}
              title="No analysis yet"
              description="Run an AI analysis to generate a positioning summary, strengths, weaknesses, content themes, and opportunities from the information you've supplied."
            />
          ) : (
            <div className="space-y-4" data-testid="ca-profile-analysis">
              <AiSourcedNote />

              <GlassCard className="p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)] mb-1">Summary</div>
                <p className="text-sm text-[color:var(--text-2)]">{s.summary || "—"}</p>
              </GlassCard>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ListCard title="Strengths" items={s.strengths} testid="ca-profile-strengths" />
                <ListCard title="Weaknesses" items={s.weaknesses} testid="ca-profile-weaknesses" />
                <ListCard title="Content themes" items={s.content_themes} testid="ca-profile-themes" />
                <ListCard title="CTA patterns" items={s.cta_patterns} testid="ca-profile-cta" />
                <ListCard title="Differentiation" items={s.differentiation} testid="ca-profile-differentiation" />
                <ListCard title="Content gaps" items={s.content_gaps} testid="ca-profile-gaps" />
              </div>

              <GlassCard className="p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)] mb-1">Target audience</div>
                <p className="text-sm text-[color:var(--text-2)]">{s.target_audience || "—"}</p>
                <div className="mt-3 text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)] mb-1">Positioning</div>
                <p className="text-sm text-[color:var(--text-2)]">{s.positioning || "—"}</p>
                <div className="mt-3 text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)] mb-1">Tone</div>
                <p className="text-sm text-[color:var(--text-2)]">{s.tone || "—"}</p>
              </GlassCard>
            </div>
          )}
        </div>
      </div>

      <CompetitorEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        initial={competitor}
        onSaved={() => { onChanged?.(); }}
      />
    </>
  );
}

function ListCard({ title, items, testid }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <GlassCard className="p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)] mb-2">{title}</div>
      {list.length === 0 ? (
        <div className="text-sm text-[color:var(--text-3)]">Not enough information.</div>
      ) : (
        <ul className="text-sm text-[color:var(--text-2)] space-y-1">
          {list.map((it, i) => (
            <li key={i} className="flex gap-2"><span className="text-[color:var(--brand-primary)]">•</span><span>{it}</span></li>
          ))}
        </ul>
      )}
    </GlassCard>
  );
}

// =============================================================
// Competitors tab
// =============================================================
function CompetitorsTab({ selectedId, setSelectedId }) {
  const [competitors, setCompetitors] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/competitors");
      setCompetitors(r.data?.competitors || []);
    } catch (_e) { /* handled by outer gate */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const selected = useMemo(
    () => competitors.find((c) => c.id === selectedId) || null,
    [competitors, selectedId],
  );

  return (
    <div className="space-y-4" data-testid="ca-tab-competitors">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-lg font-semibold">Your competitors</div>
          <div className="text-xs text-[color:var(--text-3)]">Add and manage the businesses you want to track.</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={refresh} data-testid="ca-competitors-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          <Button onClick={() => setEditorOpen(true)} data-testid="ca-competitors-add"><Plus className="h-4 w-4 mr-1" /> Add competitor</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[color:var(--text-3)] flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : competitors.length === 0 ? (
        <EmptyState
          icon={Search}
          title="See how your business stacks up."
          description="Track competitors, compare positioning and content strategy, and turn competitive gaps into marketing opportunities."
          action={<Button onClick={() => setEditorOpen(true)} data-testid="ca-competitors-empty-add"><Plus className="h-4 w-4 mr-1" /> Add your first competitor</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {competitors.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              className="text-left rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] hover:bg-[color:var(--surface-2)] p-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-primary)]/40"
              data-testid={`ca-competitor-card-${c.id}`}
            >
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-md bg-[color:var(--surface-2)] flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-[color:var(--text-2)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-[color:var(--text-3)] truncate">
                    {c.industry || "—"}{c.location ? ` · ${c.location}` : ""}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-[color:var(--text-3)]" />
              </div>
              <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">
                <Sparkles className="h-3 w-3" />
                {c.last_analyzed_at ? `Analyzed ${new Date(c.last_analyzed_at).toLocaleDateString()}` : "Not analyzed"}
              </div>
            </button>
          ))}
        </div>
      )}

      <CompetitorEditor open={editorOpen} onOpenChange={setEditorOpen} initial={null} onSaved={refresh} />

      {selected ? (
        <CompetitorProfile
          competitor={selected}
          onClose={() => setSelectedId(null)}
          onChanged={refresh}
        />
      ) : null}
    </div>
  );
}

// =============================================================
// Content Intelligence tab
// =============================================================
function ContentIntelligenceTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const cResp = await api.get("/competitors");
        const list = cResp.data?.competitors || [];
        const withAnalysis = [];
        for (const c of list) {
          try {
            const a = await api.get(`/competitors/${c.id}/analysis`);
            if (a.data?.analysis) withAnalysis.push({ competitor: c, structured: a.data.analysis.structured || {} });
          } catch (_e) { /* skip */ }
        }
        setRows(withAnalysis);
      } finally { setLoading(false); }
    })();
  }, []);

  const dimensions = [
    { key: "content_themes", label: "Content themes" },
    { key: "tone", label: "Tone", single: true },
    { key: "cta_patterns", label: "CTA patterns" },
    { key: "differentiation", label: "Differentiation" },
    { key: "content_gaps", label: "Content gaps" },
  ];

  return (
    <div className="space-y-4" data-testid="ca-tab-content">
      <div>
        <div className="font-display text-lg font-semibold">Content intelligence</div>
        <div className="text-xs text-[color:var(--text-3)]">A side-by-side view of what your competitors focus on.</div>
      </div>
      <AiSourcedNote />
      {loading ? (
        <div className="text-sm text-[color:var(--text-3)] flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Layers} title="No analyses yet" description="Analyze at least one competitor to populate this comparison." />
      ) : (
        <GlassCard className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="ca-content-matrix">
            <thead>
              <tr className="text-left border-b border-[color:var(--border-subtle)]">
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)]">Dimension</th>
                {rows.map((r) => (
                  <th key={r.competitor.id} className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)]">
                    {r.competitor.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dimensions.map((d) => (
                <tr key={d.key} className="border-b border-[color:var(--border-subtle)] align-top">
                  <td className="px-4 py-3 text-[color:var(--text-2)] font-medium whitespace-nowrap">{d.label}</td>
                  {rows.map((r) => {
                    const val = r.structured?.[d.key];
                    if (d.single) {
                      return <td key={r.competitor.id} className="px-4 py-3 text-[color:var(--text-2)]">{val || <span className="text-[color:var(--text-3)]">—</span>}</td>;
                    }
                    const arr = Array.isArray(val) ? val : [];
                    return (
                      <td key={r.competitor.id} className="px-4 py-3 text-[color:var(--text-2)]">
                        {arr.length === 0 ? <span className="text-[color:var(--text-3)]">—</span> : (
                          <ul className="space-y-1">
                            {arr.slice(0, 5).map((v, i) => <li key={i} className="flex gap-2"><span className="text-[color:var(--brand-primary)]">•</span><span>{v}</span></li>)}
                          </ul>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </GlassCard>
      )}
    </div>
  );
}

// =============================================================
// Positioning tab
// =============================================================
function PositioningTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const cResp = await api.get("/competitors");
        const list = cResp.data?.competitors || [];
        const withA = [];
        for (const c of list) {
          try {
            const a = await api.get(`/competitors/${c.id}/analysis`);
            if (a.data?.analysis) withA.push({ competitor: c, structured: a.data.analysis.structured || {} });
          } catch (_e) { /* skip */ }
        }
        setRows(withA);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-4" data-testid="ca-tab-positioning">
      <div>
        <div className="font-display text-lg font-semibold">Positioning</div>
        <div className="text-xs text-[color:var(--text-3)]">How your competitors position themselves — and where you can stand apart.</div>
      </div>
      <AiSourcedNote />

      {loading ? (
        <div className="text-sm text-[color:var(--text-3)] flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Compass} title="Nothing to compare yet" description="Analyze a competitor to see positioning insights." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((r) => {
            const axes = r.structured?.positioning_axes || {};
            return (
              <GlassCard key={r.competitor.id} className="p-5" data-testid={`ca-positioning-${r.competitor.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-4 w-4 text-[color:var(--text-2)]" />
                  <div className="font-display text-base font-semibold">{r.competitor.name}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <AxisIndicator label="Premium ↔ Value" value={axes.premium_vs_value} left="premium" right="value" />
                  <AxisIndicator label="Broad ↔ Specialized" value={axes.broad_vs_specialized} left="broad" right="specialized" />
                  <AxisIndicator label="Traditional ↔ Modern" value={axes.traditional_vs_modern} left="traditional" right="modern" />
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <ListCard title="Why customers may choose them" items={r.structured?.why_customers_choose_them} testid={`ca-positioning-them-${r.competitor.id}`} />
                  <ListCard title="Why customers may choose you" items={r.structured?.why_customers_choose_you} testid={`ca-positioning-you-${r.competitor.id}`} />
                </div>
                <div className="mt-3">
                  <ListCard title="How to differentiate" items={r.structured?.differentiation} testid={`ca-positioning-diff-${r.competitor.id}`} />
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AxisIndicator({ label, value, left, right }) {
  // Value is one of: left | balanced | right (safe strings from server)
  const pos = value === left ? "left" : value === right ? "right" : "center";
  return (
    <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)] mb-2">{label}</div>
      <div className="relative h-1.5 rounded-full bg-[color:var(--surface-1)]">
        <div className="absolute inset-0 flex items-center justify-between px-0.5 text-[9px] text-[color:var(--text-3)]">
          <span>{left}</span><span>{right}</span>
        </div>
        <div
          className="absolute top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-[color:var(--brand-primary)] shadow"
          style={{ left: pos === "left" ? "6%" : pos === "right" ? "94%" : "50%", transform: "translate(-50%, -50%)" }}
        />
      </div>
      <div className="mt-2 text-xs text-[color:var(--text-2)] capitalize">{value || "balanced"}</div>
    </div>
  );
}

// =============================================================
// Opportunities tab
// =============================================================
function OpportunitiesTab() {
  const [opps, setOpps] = useState([]);
  const [state, setState] = useState("saved");
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/competitor-opportunities", { params: { state } });
      const sorted = [...(r.data?.opportunities || [])].sort(
        (a, b) => (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3),
      );
      setOpps(sorted);
    } finally { setLoading(false); }
  }, [state]);

  useEffect(() => { refresh(); }, [refresh]);

  const setOppState = async (id, next) => {
    try {
      await api.patch(`/competitor-opportunities/${id}`, { state: next });
      toast.success(next === "acted_on" ? "Marked as acted on." : next === "dismissed" ? "Dismissed." : "Restored.");
      refresh();
    } catch (_e) { toast.error("Could not update opportunity."); }
  };

  return (
    <div className="space-y-4" data-testid="ca-tab-opportunities">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-display text-lg font-semibold">Opportunities</div>
          <div className="text-xs text-[color:var(--text-3)]">Turn competitor intelligence into action.</div>
        </div>
        <div className="inline-flex rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-0.5" role="tablist">
          {["saved", "acted_on", "dismissed"].map((s) => (
            <button
              key={s}
              onClick={() => setState(s)}
              data-testid={`ca-opps-state-${s}`}
              className={`px-3 py-1.5 text-xs rounded ${state === s ? "bg-[color:var(--surface-2)] text-[color:var(--text-1)]" : "text-[color:var(--text-3)] hover:text-[color:var(--text-2)]"}`}
            >
              {s === "acted_on" ? "Acted on" : s === "dismissed" ? "Dismissed" : "Saved"}
            </button>
          ))}
        </div>
      </div>

      <AiSourcedNote />

      {loading ? (
        <div className="text-sm text-[color:var(--text-3)] flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : opps.length === 0 ? (
        <EmptyState icon={Lightbulb} title="No opportunities yet" description="Analyze a competitor to surface prioritized content, messaging, and differentiation opportunities." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {opps.map((o) => (
            <GlassCard key={o.id} className="p-4" data-testid={`ca-opp-${o.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <PriorityBadge priority={o.priority} />
                <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">
                  {CATEGORY_LABELS[o.category] || o.category}
                </span>
              </div>
              <div className="font-display text-base font-semibold">{o.title}</div>
              {o.reasoning ? <p className="text-sm text-[color:var(--text-2)] mt-2">{o.reasoning}</p> : null}
              {o.recommended_action ? (
                <div className="mt-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)] mb-1">Recommended action</div>
                  <div className="text-sm text-[color:var(--text-2)]">{o.recommended_action}</div>
                </div>
              ) : null}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {state !== "acted_on" ? (
                  <Button variant="outline" size="sm" onClick={() => setOppState(o.id, "acted_on")} data-testid={`ca-opp-act-${o.id}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Mark as acted on
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setOppState(o.id, "saved")} data-testid={`ca-opp-restore-${o.id}`}>
                    <Circle className="h-4 w-4 mr-1" /> Restore
                  </Button>
                )}
                {state !== "dismissed" ? (
                  <Button variant="ghost" size="sm" onClick={() => setOppState(o.id, "dismissed")} data-testid={`ca-opp-dismiss-${o.id}`}>
                    <Ban className="h-4 w-4 mr-1" /> Dismiss
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => setOppState(o.id, "saved")} data-testid={`ca-opp-restore-${o.id}`}>
                    <Circle className="h-4 w-4 mr-1" /> Restore
                  </Button>
                )}
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button variant="ghost" size="sm" disabled data-testid={`ca-opp-generate-${o.id}`}>
                          <Sparkles className="h-4 w-4 mr-1" /> Generate post <span className="ml-1 text-[9px] uppercase tracking-wider text-[color:var(--text-3)]">Coming next</span>
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Cross-feature actions arrive in Phase 2.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button variant="ghost" size="sm" disabled data-testid={`ca-opp-send-ideas-${o.id}`}>
                          <ArrowRight className="h-4 w-4 mr-1" /> Send to Marketing Ideas <span className="ml-1 text-[9px] uppercase tracking-wider text-[color:var(--text-3)]">Coming next</span>
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Cross-feature actions arrive in Phase 2.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================
// Reports tab
// =============================================================
function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/competitor-reports");
      setReports(r.data?.reports || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const createReport = async () => {
    const nm = (name || "").trim();
    if (!nm) { toast.error("Please give the report a name."); return; }
    try {
      // Compose a report from currently stored competitors + analyses + opps.
      const [cResp, oResp] = await Promise.all([
        api.get("/competitors"),
        api.get("/competitor-opportunities", { params: { state: "saved" } }),
      ]);
      const list = cResp.data?.competitors || [];
      const cids = list.map((c) => c.id);
      const analyses = [];
      for (const c of list) {
        try {
          const a = await api.get(`/competitors/${c.id}/analysis`);
          if (a.data?.analysis) analyses.push({ name: c.name, s: a.data.analysis.structured || {} });
        } catch (_e) { /* skip */ }
      }
      const opps = oResp.data?.opportunities || [];
      const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));
      const sections = {
        exec_summary: analyses.length
          ? `AI competitive analysis based on ${analyses.length} competitor${analyses.length === 1 ? "" : "s"} you've supplied. No external websites or social profiles were fetched.`
          : "No analyses have been run yet.",
        competitor_overview: analyses.map((a) => `${a.name}: ${a.s.summary || ""}`).slice(0, 8),
        strengths: uniq(analyses.flatMap((a) => a.s.strengths || [])).slice(0, 12),
        weaknesses: uniq(analyses.flatMap((a) => a.s.weaknesses || [])).slice(0, 12),
        content_themes: uniq(analyses.flatMap((a) => a.s.content_themes || [])).slice(0, 12),
        positioning_diffs: uniq(analyses.flatMap((a) => a.s.differentiation || [])).slice(0, 12),
        market_gaps: uniq(analyses.flatMap((a) => a.s.content_gaps || [])).slice(0, 12),
        opportunities: opps.slice(0, 12).map((o) => `[${o.priority}] ${o.title}`),
        next_actions: uniq(analyses.flatMap((a) => a.s.recommended_actions || [])).slice(0, 10),
      };
      await api.post("/competitor-reports", { name: nm, competitor_ids: cids, sections });
      toast.success("Report saved.");
      setDialogOpen(false); setName("");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save report."); }
  };

  const deleteReport = async (id) => {
    try {
      await api.delete(`/competitor-reports/${id}`);
      toast.success("Report deleted.");
      if (selected?.id === id) setSelected(null);
      refresh();
    } catch (_e) { toast.error("Could not delete report."); }
  };

  const saveRename = async (id) => {
    const nm = (renameValue || "").trim();
    if (!nm) { toast.error("Report name cannot be empty."); return; }
    try {
      await api.patch(`/competitor-reports/${id}`, { name: nm });
      toast.success("Report renamed.");
      setRenamingId(null); setRenameValue("");
      refresh();
    } catch (_e) { toast.error("Could not rename report."); }
  };

  return (
    <div className="space-y-4" data-testid="ca-tab-reports">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-lg font-semibold">Reports</div>
          <div className="text-xs text-[color:var(--text-3)]">Save a snapshot of the current competitive landscape for future reference.</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={refresh} data-testid="ca-reports-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="ca-reports-new"><Plus className="h-4 w-4 mr-1" /> New report</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-[color:var(--bg-1)] border-[color:var(--border-subtle)]">
              <DialogHeader><DialogTitle>New competitive analysis report</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Label htmlFor="ca-report-name">Report name</Label>
                <Input id="ca-report-name" data-testid="ca-reports-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Q1 competitive landscape" />
                <div className="text-xs text-[color:var(--text-3)]">A snapshot of your current competitors, analyses, and open opportunities will be saved into this report.</div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={createReport} data-testid="ca-reports-create"><Save className="h-4 w-4 mr-1" /> Save report</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[color:var(--text-3)] flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : reports.length === 0 ? (
        <EmptyState icon={FileText} title="No reports yet" description="Save a competitive analysis report to revisit later, or use browser print to export a clean copy." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 space-y-2" data-testid="ca-reports-list">
            {reports.map((r) => (
              <div key={r.id} className={`rounded-md border px-3 py-2 flex items-center gap-2 ${selected?.id === r.id ? "border-[color:rgba(27,168,159,0.35)] bg-[color:var(--surface-2)]" : "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]"}`} data-testid={`ca-report-${r.id}`}>
                {renamingId === r.id ? (
                  <>
                    <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} className="h-8" data-testid={`ca-report-rename-input-${r.id}`} />
                    <Button size="sm" onClick={() => saveRename(r.id)} data-testid={`ca-report-rename-save-${r.id}`}><Save className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => { setRenamingId(null); setRenameValue(""); }}><X className="h-3.5 w-3.5" /></Button>
                  </>
                ) : (
                  <>
                    <button className="flex-1 text-left" onClick={() => setSelected(r)} data-testid={`ca-report-open-${r.id}`}>
                      <div className="text-sm font-medium">{r.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">
                        {new Date(r.created_at).toLocaleString()}
                      </div>
                    </button>
                    <Button size="icon" variant="ghost" onClick={() => { setRenamingId(r.id); setRenameValue(r.name); }} data-testid={`ca-report-rename-${r.id}`} aria-label="Rename">
                      <Edit3 className="h-3.5 w-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" data-testid={`ca-report-delete-${r.id}`} aria-label="Delete">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[color:var(--bg-1)] border-[color:var(--border-subtle)]">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete report?</AlertDialogTitle>
                          <AlertDialogDescription>“{r.name}” will be removed. This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteReport(r.id)} data-testid={`ca-report-delete-confirm-${r.id}`}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            ))}
          </div>
          <GlassCard className="lg:col-span-2 p-5" data-testid="ca-report-viewer">
            {!selected ? (
              <div className="text-sm text-[color:var(--text-3)]">Select a report to view.</div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-lg font-semibold">{selected.name}</div>
                    <div className="text-xs text-[color:var(--text-3)]">Saved {new Date(selected.created_at).toLocaleString()}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => window.print()} data-testid="ca-report-print"><FileText className="h-4 w-4 mr-1" /> Print / PDF</Button>
                </div>
                <AiSourcedNote />
                <ReportSection title="Executive summary" value={selected.sections?.exec_summary} />
                <ReportListSection title="Competitor overview" items={selected.sections?.competitor_overview} />
                <ReportListSection title="Key strengths" items={selected.sections?.strengths} />
                <ReportListSection title="Key weaknesses" items={selected.sections?.weaknesses} />
                <ReportListSection title="Content themes" items={selected.sections?.content_themes} />
                <ReportListSection title="Positioning differences" items={selected.sections?.positioning_diffs} />
                <ReportListSection title="Market gaps" items={selected.sections?.market_gaps} />
                <ReportListSection title="Recommended opportunities" items={selected.sections?.opportunities} />
                <ReportListSection title="Suggested next actions" items={selected.sections?.next_actions} />
              </div>
            )}
          </GlassCard>
        </div>
      )}
    </div>
  );
}

function ReportSection({ title, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)] mb-1">{title}</div>
      <p className="text-sm text-[color:var(--text-2)]">{value || <span className="text-[color:var(--text-3)]">—</span>}</p>
    </div>
  );
}

function ReportListSection({ title, items }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)] mb-1">{title}</div>
      {list.length === 0 ? (
        <p className="text-sm text-[color:var(--text-3)]">—</p>
      ) : (
        <ul className="text-sm text-[color:var(--text-2)] space-y-1">
          {list.map((it, i) => <li key={i} className="flex gap-2"><span className="text-[color:var(--brand-primary)]">•</span><span>{it}</span></li>)}
        </ul>
      )}
    </div>
  );
}

// =============================================================
// Root page
// =============================================================
export default function CompetitorAnalysisPage() {
  const [access, setAccess] = useState(null);
  const [tab, setTab] = useState("overview");
  const [selectedCompetitorId, setSelectedCompetitorId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/competitors/access");
        if (!cancelled) setAccess(r.data);
      } catch (_e) {
        if (!cancelled) setAccess({ allowed: false, reason: "coming_soon", plan: "free" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!access) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-sm text-[color:var(--text-3)] flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="competitor-analysis-page">
      <PageHeader
        title="Competitor Analysis"
        subtitle={access.allowed
          ? (access.reason === "owner_dev"
              ? "Owner development access — track competitors, analyze their positioning and content, and turn gaps into marketing opportunities."
              : "Track competitors, analyze their positioning and content, and turn gaps into marketing opportunities.")
          : "Coming Soon — this feature is under development."}
        testidPrefix="competitor-analysis"
      />
      {!access.allowed ? (
        <LockedComingSoon access={access} />
      ) : (
        <Tabs value={tab} onValueChange={setTab} data-testid="ca-tabs">
          <TabsList className="flex flex-wrap gap-1 bg-[color:var(--surface-1)] border border-[color:var(--border-subtle)]">
            <TabsTrigger value="overview" data-testid="ca-tab-btn-overview"><BarChart3 className="h-4 w-4 mr-1" /> Overview</TabsTrigger>
            <TabsTrigger value="competitors" data-testid="ca-tab-btn-competitors"><Target className="h-4 w-4 mr-1" /> Competitors</TabsTrigger>
            <TabsTrigger value="content" data-testid="ca-tab-btn-content"><Layers className="h-4 w-4 mr-1" /> Content Intelligence</TabsTrigger>
            <TabsTrigger value="positioning" data-testid="ca-tab-btn-positioning"><Compass className="h-4 w-4 mr-1" /> Positioning</TabsTrigger>
            <TabsTrigger value="opportunities" data-testid="ca-tab-btn-opportunities"><Lightbulb className="h-4 w-4 mr-1" /> Opportunities</TabsTrigger>
            <TabsTrigger value="reports" data-testid="ca-tab-btn-reports"><FileText className="h-4 w-4 mr-1" /> Reports</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4"><OverviewTab setTab={setTab} /></TabsContent>
          <TabsContent value="competitors" className="mt-4"><CompetitorsTab selectedId={selectedCompetitorId} setSelectedId={setSelectedCompetitorId} /></TabsContent>
          <TabsContent value="content" className="mt-4"><ContentIntelligenceTab /></TabsContent>
          <TabsContent value="positioning" className="mt-4"><PositioningTab /></TabsContent>
          <TabsContent value="opportunities" className="mt-4"><OpportunitiesTab /></TabsContent>
          <TabsContent value="reports" className="mt-4"><ReportsTab /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
